import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Button, Row, Col, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Form,
  Select,
  Table,
  InputNumber,
  DatePicker,
  Input,
  TableColumnsType,
  Space,
  Tag,
  Empty,
  Alert as AntAlert,
  Descriptions,
  Divider,
  Modal,
  Upload,
  Button as AntButton,
  Flex,
  Spin,
  message,
  Popover,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import RABillApis from "../../api/WorkOrderManagement/RABillApis";
import paymentApis from "../../api/WorkOrderManagement/PaymentsApis";
import {
  PaymentType,
  PaymentMethod,
  ApprovedRaBillData,
  PaymentAllocation,
} from "./types";
import { getEligibleRaBillsForPayment } from "./mockData/approvedRaBillsData";
import { mockPaymentMethodOptions } from "./mockData/paymentData";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";
import sequenceApis from "../../api/master/sequence";
import PaymentsApprovalDrawer from "./PaymentsApprovalDrawer";
import { indianCurrencyFormatter, indianCurrencyParser } from "../../utils/Utils";

const { Option } = Select;
const { TextArea } = Input;

interface FormData {
  paymentVoucherNumber: string;
  paymentDate: dayjs.Dayjs | null;
  paymentType: PaymentType;
  contractorId: string;
  legalEntityId: string;
  paymentMethod: PaymentMethod;
  bankAccountId: string;
  tdsSection: string;
  tdsRate: number;
  narration: string;
  allocations: PaymentAllocation[];
}

const CreateReceivable: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const duplicateId = searchParams.get("duplicate");
  const isEdit = Boolean(editId);
  const isDuplicate = Boolean(duplicateId);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRaBills, setSelectedRaBills] = useState<ApprovedRaBillData[]>(
    []
  );
  const [tempSelectedRaBills, setTempSelectedRaBills] = useState<
    ApprovedRaBillData[]
  >([]);
  const [eligibleRaBills, setEligibleRaBills] = useState<any[]>([]);
  const [rabillCount, setRABillCount] = useState(0);
  const [showEligibleBills, setShowEligibleBills] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<string>("");
  const [raBillsLoading, setRaBillsLoading] = useState(false);
  const [raBillsPagination, setRaBillsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [contractorLoading, setContractorLoading] = useState(false);
  const [sequenceAccess, setSequenceAccess] = useState<any>();
  const [paymentSequenceId, setPaymentSequenceId] = useState("");
  const [siteError, setSiteError] = useState<{
    show: boolean;
    siteNames: string[];
  }>({ show: false, siteNames: [] });

  // Approval related state
  const [approvalRequired, setApprovalRequired] = useState<string>("false");
  const [approvals, setApprovals] = useState<{
    open: boolean;
    data: any[];
  }>({ open: false, data: [] });
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);

  // Commercial calculations
  const [sumOfAllocations, setSumOfAllocations] = useState(0);
  const [tdsThisPayment, setTdsThisPayment] = useState(0);
  const [otherAdjustments, setOtherAdjustments] = useState(0);
  const [netPayable, setNetPayable] = useState(0);

  useEffect(() => {
    // loadEligibleRaBills();
    initializeForm();
    loadContractorOptions();
  }, []);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadEligibleRaBills = async (params?: any) => {
    setRaBillsLoading(true);
    try {
      const {
        current = 1,
        sortOrder = "DESC",
        pageSize = 10,
        direction = "P",
        pageDiff = 0,
        paginationId = null,
        searchQuery = null,
        selectFilter1 = null,
        startDate = null,
        endDate = null,
        contractorIds = null,
      } = params || {};

      const apiResponse: any = await RABillApis.listRABill({
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
        status: selectFilter1,
        startDate,
        endDate,
        filterListType: "OutStanding",
        contractorIds,
        flowType: "Client",
      });

      const { data, status } = apiResponse;
      if (status) {
        const { list, count } = data || {};
        setRABillCount(count);
        setEligibleRaBills(list || []);
        setRaBillsPagination((prev) => ({
          ...prev,
          total: Number(count) || 0,
          current: Number(current),
          pageSize: Number(pageSize),
        }));
      }
    } catch (error) {
      console.error("Error loading eligible RA bills:", error);
      // Fallback to mock data if API fails
      const eligible = getEligibleRaBillsForPayment();
      setEligibleRaBills(eligible);
      setRaBillsPagination((prev) => ({
        ...prev,
        total: eligible.length,
      }));
    } finally {
      setRaBillsLoading(false);
    }
  };

  const initializeForm = () => {
    // Set default values
    form.setFieldsValue({
      paymentVoucherNumber: generatePaymentVoucherNumber(),
      paymentDate: dayjs(),
      paymentType: PaymentType.RA,
      tdsRate: 1.0,
      approvalRequired: "false",
    });
  };

  const generatePaymentVoucherNumber = () => {
    const currentDate = dayjs();
    const year = currentDate.year();
    const month = currentDate.format("MM");
    const randomNum = Math.floor(Math.random() * 9999)
      .toString()
      .padStart(4, "0");
    return `REC/${year}/HYD/${randomNum}`;
  };

  const fetchSequenceAccess = async (siteId: number) => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Receivables" &&
          new Date(item.endDate) > today &&
          item.status === "Active" &&
          item.siteId === siteId
      );
      if (_sequenceAccess.length > 0) {
        setSequenceAccess({
          module: "Receivables",
          level: _sequenceAccess[0].level,
          siteIds: _sequenceAccess?.map((item: any) =>
            item?.siteId?.toString()
          ),
        });
      } else {
        setSequenceAccess({
          module: "",
          level: "",
          siteIds: [],
        });
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
    }
  };

  const loadContractorOptions = async () => {
    setContractorLoading(true);
    try {
      // For Client Receivables, pass partyType as "Client"
      const response = await dropdownApis.fetchContractorDropDown("Client");
      if (response.status && response.data) {
        const options = response.data.map((contractor: any) => ({
          value: contractor.contractorID.toString(),
          label: contractor.companyName,
          contractorOrgId: contractor.contractorOrgId,
          contactPerson: contractor.contactPerson,
        }));
        setContractorOptions(options);
      }
    } catch (error) {
      console.error("Error loading contractor options:", error);
      // message.error("Error loading contractor options");
      // Fallback to mock data if API fails
      // setContractorOptions(mockContractorOptions);
    } finally {
      setContractorLoading(false);
    }
  };

  const handleContractorChange = async (contractorId: string) => {
    setSelectedContractor(contractorId);

    // Clear existing selections if contractor changes
    setSelectedRaBills([]);
    setTempSelectedRaBills([]);
    form.setFieldsValue({ allocations: [] });
    calculateTotals([]);

    // Reload RA bills for the selected contractor
    await loadEligibleRaBills({
      current: 1,
      pageSize: raBillsPagination.pageSize,
      sortOrder: "DESC",
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      contractorIds: contractorId, // Pass contractor ID to filter
    });
  };

  const showRaBillSelection = () => {
    if (!selectedContractor) {
      Swal.fire("Warning", "Please select a contractor first", "warning");
      return;
    }
    // Initialize temp selection with currently selected bills
    setTempSelectedRaBills([...selectedRaBills]);
    setShowEligibleBills(true);
  };

  const handleTempRaBillSelection = (
    selectedRowKeys: React.Key[],
    selectedRows: ApprovedRaBillData[]
  ) => {
    // Validate that all selected RA bills are from the same site
    if (selectedRows.length > 0) {
      const siteIds = selectedRows.map((bill) => bill.siteId);
      const uniqueSiteIds = Array.from(new Set(siteIds));

      if (uniqueSiteIds.length > 1) {
        // Get site names for better error message
        const siteNames = selectedRows
          .map((bill) => `${bill.siteName} (Site ID: ${bill.siteId})`)
          .filter((v, i, a) => a.indexOf(v) === i);

        setSiteError({
          show: true,
          siteNames: siteNames,
        });
        return; // Don't update the selection
      }
    }

    // Clear error if selection is valid
    setSiteError({ show: false, siteNames: [] });
    setTempSelectedRaBills(selectedRows);
  };

  const handleRaBillSelection = (
    selectedRowKeys: React.Key[],
    selectedRows: ApprovedRaBillData[]
  ) => {
    setSelectedRaBills(selectedRows);

    // Create allocations for selected bills
    const allocations: any[] = selectedRows.map((bill: any, index) => ({
      id: `ALLOC${String(index + 1).padStart(3, "0")}`,
      raBillId: bill.raBillId,
      raBillNumber: bill.raBillSequenceId,
      workOrderId: bill.workOrderId,
      workOrderNumber: bill.workOrderNumber,
      raPeriod: bill.raPeriodFrom + " - " + bill.raPeriodTo,
      raBillValue: bill.netPayable,
      alreadyPaid: bill.paidAmount,
      billBalance: bill.balanceAmount,
      thisPaymentAmount: bill.balanceAmount, // Default to full balance
      resultingStatus: "Paid" as const,
    }));

    form.setFieldsValue({ allocations });
    calculateTotals(allocations);
  };

  const confirmRaBillSelection = () => {
    handleRaBillSelection(
      tempSelectedRaBills.map((bill: any) => bill.raBillId),
      tempSelectedRaBills
    );
    // Fetch sequence access based on the site of selected RA bills
    if (tempSelectedRaBills.length > 0) {
      const siteId = Number(tempSelectedRaBills[0].siteId);
      fetchSequenceAccess(siteId);
    }
    setShowEligibleBills(false);
  };

  const cancelRaBillSelection = () => {
    setTempSelectedRaBills([...selectedRaBills]); // Reset temp selection
    setSiteError({ show: false, siteNames: [] }); // Clear error
    setShowEligibleBills(false);
  };

  const calculateTotals = (allocations: PaymentAllocation[]) => {
    const sum = allocations.reduce(
      (total, allocation) => total + allocation.thisPaymentAmount,
      0
    );
    setSumOfAllocations(sum);

    const tdsRate = form.getFieldValue("tdsRate") || 1.0;
    const tds = (sum * tdsRate) / 100;
    setTdsThisPayment(tds);

    const net = sum;
    setNetPayable(net);
  };

  const handleAllocationChange = (
    index: number,
    field: string,
    value: number
  ) => {
    const allocations = form.getFieldValue("allocations") || [];
    allocations[index] = { ...allocations[index], [field]: value };

    // Update resulting status
    if (field === "thisPaymentAmount") {
      const allocation = allocations[index];
      if (value >= allocation.billBalance) {
        allocation.resultingStatus = "Paid";
      } else if (value > 0) {
        allocation.resultingStatus = "Partially Paid";
      } else {
        allocation.resultingStatus = "Unpaid";
      }
    }

    form.setFieldsValue({ allocations });
    calculateTotals(allocations);
  };

  const removeAllocation = (index: number) => {
    const allocations = form.getFieldValue("allocations") || [];
    const updatedAllocations = allocations.filter(
      (_: any, i: number) => i !== index
    );
    form.setFieldsValue({ allocations: updatedAllocations });

    // Update selected RA bills
    const updatedSelectedBills = selectedRaBills.filter((_, i) => i !== index);
    setSelectedRaBills(updatedSelectedBills);

    calculateTotals(updatedAllocations);
  };

  const handleTdsRateChange = (value: number | null) => {
    if (value !== null) {
      const allocations = form.getFieldValue("allocations") || [];
      calculateTotals(allocations);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Validate approvals are set when approval is required
      if (values.approvalRequired === "true" && approvals.data.length === 0) {
        Swal.fire({
          icon: "error",
          title: "Approval Required",
          text: "Please set the required approvals before submitting the receivable.",
        });
        return;
      }

      setLoading(true);

      // Prepare receivable lines from allocations
      const lines = (values.allocations || []).map((allocation: any) => ({
        raBillId: allocation.raBillId,
        paidAmount: allocation.thisPaymentAmount,
        remarks: `Receivable for RA Bill ${allocation.raBillNumber}`,
      }));

      // Format approval hierarchy according to required structure
      const approvalHierarchy = approvals.data.map((approval: any) => ({
        userId: parseInt(approval.userId),
        roleId: approval.roleId,
        level: approval.level,
        approvalStage: approval.approvalStage || "",
      }));

      // Create payment payload according to API format
      const paymentPayload = {
        contractorId: parseInt(values.contractorId),
        ...(paymentSequenceId && {
          raBillPaymentSequenceId: paymentSequenceId,
        }),
        projectId: selectedRaBills[0]?.projectId || null, // Take from first selected bill
        // paymentReference: values.paymentVoucherNumber,
        // transactionNumber: values.paymentVoucherNumber,
        utrNumber: values.utrNumber || null, // For NEFT/RTGS/IMPS
        chequeNumber: values.chequeNumber || null, // For Cheque
        transactionNumber: values.transactionNumber || null, // For UPI
        paymentMethod: values.paymentMethod,
        paymentType: "RaBill",
        paymentDate: values.paymentDate?.format("YYYY-MM-DD"),
        bankAccountId: parseInt(values.bankAccountId) || null,
        bankAccountNumber: "", // Can be fetched from bank account details
        bankName: "", // Can be fetched from bank account details
        totalPaymentAmount: sumOfAllocations,
        tdsSection: values.tdsSection || "",
        tdsRate: values.tdsRate || 0,
        otherDeductions: otherAdjustments,
        deductionReason: otherAdjustments > 0 ? "Other adjustments" : "",
        remarks: values.narration || "",
        narration: values.narration || "",
        currencyId: 1, // Default currency
        approvalRequired: values.approvalRequired === "true",
        isStrictApproval: values.approvalRequired === "true" ? isStrictApproval : false,
        isDraft: false,
        lines: lines,
        approvalHierarchy:
          values.approvalRequired === "true" ? approvalHierarchy : [],
        flowType: "Client", // Pass flowType as "Client" for receivables
      };

      console.log("Payment payload:", paymentPayload);

      // Create FormData for multipart/form-data submission
      const formData = new FormData();

      // Append main payment data as JSON string
      formData.append("payment", JSON.stringify(paymentPayload));

      // Append files if any
      if (fileList && fileList.length > 0) {
        fileList.forEach((file, index) => {
          if (file.originFileObj) {
            formData.append(`attachments[${index}]`, file.originFileObj);
          }
        });
      }

      // Call the create receivable API with FormData (using payment API for now)
      const apiResponse: any = await paymentApis.createPayment(formData);

      if (apiResponse.status) {
        Swal.fire("Success!", apiResponse.data.message, "success").then(() => {
          navigate(-1);
        });
      } else {
        throw new Error(apiResponse.message || "Failed to create receivable");
      }
    } catch (error: any) {
      console.error("Error creating receivable:", error);
      // Don't show Swal for form validation errors (errorFields means it's a validation error)
      if (error?.errorFields) {
        // Form validation failed - field-level errors are already shown
        return;
      }
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create receivable. Please try again.";
      Swal.fire("Error!", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // File upload handlers
  const handleFileUpload = async (file: any) => {
    setUploadLoading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", "receivable_attachment");
      formData.append("moduleId", "receivables");

      // Here you would typically call a file upload API
      // const uploadResponse = await fileUploadApi.uploadFile(formData);

      // For now, simulate file upload success
      const uploadedFile = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: URL.createObjectURL(file), // Temporary URL for preview
        response: {
          fileId: `file_${Date.now()}`,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadDate: new Date().toISOString(),
        },
      };

      setFileList((prev) => [...prev, uploadedFile]);
      return uploadedFile;
    } catch (error) {
      console.error("File upload failed:", error);
      throw error;
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileRemove = (file: any) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const beforeUpload = (file: any) => {
    const isValidType = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/xlsx",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type);
    if (!isValidType) {
      Swal.fire(
        "Error!",
        "You can only upload JPG/PNG/PDF/DOC/DOCX files!",
        "error"
      );
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      Swal.fire("Error!", "File must be smaller than 10MB!", "error");
      return false;
    }
    return true;
  };

  // RA Bills Selection Columns
  const raBillColumns: TableColumnsType<ApprovedRaBillData> = [
    {
      title: "RA Bill Number",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 140,
    },
    {
      title: "Work Order",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 140,
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      // width: 250,
      render: (text: string, record: any, index: any) => {
        return text;
      },
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (record: ApprovedRaBillData) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          {record.projectName && (
            <small className="text-muted">{record.projectName}</small>
          )}
        </div>
      ),
    },
    {
      title: "RA Period",
      dataIndex: "raPeriod",
      key: "raPeriod",
      width: 200,
      render: (raPeriod, record: any) =>
        `${record.raPeriodFrom} to ${record.raPeriodTo}`,
    },
    {
      title: "Balance Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (record: ApprovedRaBillData) => (
        <div>
          <Tag color={record.status === "Approved" ? "green" : "blue"}>
            {record.status}
          </Tag>
          {record.hasSoftHold && <Tag color="orange">Soft Hold</Tag>}
        </div>
      ),
    },
  ];

  // Payment Allocations Columns
  const allocationColumns: TableColumnsType<any> = [
    {
      title: "#",
      key: "#",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "RA Bill Number",
      dataIndex: "raBillNumber",
      key: "raBillNumber",
      width: 140,
    },
    {
      title: "Work Order",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 140,
    },
    {
      title: "RA Period",
      dataIndex: "raPeriod",
      key: "raPeriod",
      width: 180,
    },
    {
      title: "Bill Value",
      dataIndex: "raBillValue",
      key: "raBillValue",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Paid (Till Date)",
      dataIndex: "alreadyPaid",
      key: "alreadyPaid",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "Balance",
      dataIndex: "billBalance",
      key: "billBalance",
      width: 120,
      render: (amount: number) => `₹${amount?.toLocaleString()}`,
    },
    {
      title: "This Receivable",
      dataIndex: "thisPaymentAmount",
      key: "thisPaymentAmount",
      width: 140,
      render: (amount: number, _: any, index: number) => (
        <InputNumber
          value={amount}
          min={0}
          max={form.getFieldValue("allocations")?.[index]?.billBalance || 0}
          formatter={indianCurrencyFormatter}
          parser={indianCurrencyParser}
          onChange={(value) =>
            handleAllocationChange(index, "thisPaymentAmount", value || 0)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    // {
    //   title: "Resulting Status",
    //   dataIndex: "resultingStatus",
    //   key: "resultingStatus",
    //   width: 120,
    //   render: (status: string) => {
    //     const color =
    //       status === "Paid"
    //         ? "green"
    //         : status === "Partially Paid"
    //         ? "orange"
    //         : "red";
    //     return <Tag color={color}>{status}</Tag>;
    //   },
    // },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: any, __: any, index: number) => (
        <AntButton
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeAllocation(index)}
        />
      ),
    },
  ];

  return (
    <>
      <Head title="Create Receivable" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg">
            <BlockHead size="lg">
              <BlockBetween>
                <BlockHeadContent>
                  <BlockTitle tag="h4">
                    {isEdit
                      ? "Edit Receivable"
                      : isDuplicate
                        ? "Duplicate Receivable"
                        : "Create Receivable"}
                  </BlockTitle>
                </BlockHeadContent>
                <div className="d-flex">
                  <div className="actions clearfix">
                    <ul
                      className="m-auto d-flex align-items-center justify-content-center p-0"
                      style={{ flexDirection: "row-reverse" }}
                    >
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          onClick={() => navigate(-1)}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>
              </BlockBetween>
            </BlockHead>

            <div className="content clearfix">
              <Form form={form} layout="vertical" size="large">
                {/* Basic Information */}
                <Block>
                  {/* <h5>Basic Information</h5> */}
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Form.Item
                        name="paymentDate"
                        label="Receivable Date"
                        rules={[
                          {
                            required: true,
                            message: "Receivable date is required",
                          },
                        ]}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="paymentType"
                        label="Receivable Type"
                        rules={[
                          {
                            required: true,
                            message: "Receivable type is required",
                          },
                        ]}
                      >
                        <Select>
                          <Option value={PaymentType.RA}>RA Payment</Option>
                          <Option value={PaymentType.RetentionRelease}>
                            Release Retaintion Amount
                          </Option>
                          <Option value={PaymentType.OnHold}>
                            Release OnHold Amount
                          </Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        name="contractorId"
                        label="Client"
                        rules={[
                          { required: true, message: "Client is required" },
                        ]}
                      >
                        <Select
                          placeholder="Select client"
                          showSearch
                          loading={contractorLoading}
                          optionFilterProp="children"
                          onChange={handleContractorChange}
                        >
                          {contractorOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="paymentMethod"
                        label="Payment Method"
                        rules={[
                          {
                            required: true,
                            message: "Payment method is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select payment method">
                          {mockPaymentMethodOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Conditional fields based on Payment Method */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.paymentMethod !== currentValues.paymentMethod
                      }
                    >
                      {({ getFieldValue }) => {
                        const paymentMethod = getFieldValue("paymentMethod");

                        // Show UTR Number for NEFT, RTGS, IMPS
                        if (
                          paymentMethod === PaymentMethod.NEFT ||
                          paymentMethod === PaymentMethod.RTGS ||
                          paymentMethod === PaymentMethod.IMPS
                        ) {
                          return (
                            <Col span={8}>
                              <Form.Item
                                name="utrNumber"
                                label="UTR Number"
                                rules={[
                                  {
                                    required: true,
                                    message: "UTR number is required",
                                  },
                                ]}
                              >
                                <Input placeholder="Enter UTR number" />
                              </Form.Item>
                            </Col>
                          );
                        }

                        // Show Cheque Number for Cheque
                        if (paymentMethod === PaymentMethod.Cheque) {
                          return (
                            <Col span={8}>
                              <Form.Item
                                name="chequeNumber"
                                label="Cheque Number"
                                rules={[
                                  {
                                    required: true,
                                    message: "Cheque number is required",
                                  },
                                ]}
                              >
                                <Input placeholder="Enter cheque number" />
                              </Form.Item>
                            </Col>
                          );
                        }

                        // Show Transaction Number for UPI
                        if (paymentMethod === PaymentMethod.UPI) {
                          return (
                            <Col span={8}>
                              <Form.Item
                                name="transactionNumber"
                                label="Transaction Number"
                                rules={[
                                  {
                                    required: true,
                                    message: "Transaction number is required",
                                  },
                                ]}
                              >
                                <Input placeholder="Enter transaction number" />
                              </Form.Item>
                            </Col>
                          );
                        }

                        return null;
                      }}
                    </Form.Item>

                    {/* <Col span={12}>
                      <Form.Item
                        name="legalEntityId"
                        label="Legal Entity"
                        rules={[
                          {
                            required: true,
                            message: "Legal entity is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select legal entity">
                          {mockLegalEntityOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col> */}
                  </Row>

                  <Row gutter={[16, 16]}>
                    {/* <Col span={8}>
                      <Form.Item
                        name="bankAccountId"
                        label="Bank Account"
                        rules={[
                          {
                            required: true,
                            message: "Bank account is required",
                          },
                        ]}
                      >
                        <Select placeholder="Select bank account">
                          {mockBankAccountOptions.map((option) => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col> */}
                    {/* <Col span={8}>
                      <Form.Item
                        name="tdsSection"
                        label="TDS Section"
                        rules={[{ required: true, message: "TDS section is required" }]}
                      >
                        <Select placeholder="Select TDS section">
                          {mockTdsSectionOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col> */}
                  </Row>

                  <Row gutter={[16, 16]}>
                    {/* <Col span={6}>
                      <Form.Item
                        name="tdsRate"
                        label="TDS Rate (%)"
                        rules={[{ required: true, message: "TDS rate is required" }]}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          step={0.1}
                          style={{ width: "100%" }}
                          onChange={handleTdsRateChange}
                        />
                      </Form.Item>
                    </Col> */}
                    <Col span={18}>
                      <Form.Item name="narration" label="Narration">
                        <TextArea
                          rows={2}
                          placeholder="Enter receivable narration"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* RA Bill Selection */}
                <Block>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Receivable Allocations</h5>
                    <AntButton
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={showRaBillSelection}
                      disabled={!selectedContractor}
                    >
                      Add RA Bills {selectedContractor && `(${rabillCount})`}
                    </AntButton>
                  </div>

                  <Form.Item name="allocations">
                    {selectedRaBills.length === 0 ? (
                      <div className="text-center py-4">
                        <Empty description="No RA bills selected. Click 'Add RA Bills' to select bills for receivable." />
                      </div>
                    ) : (
                      <Table
                        columns={allocationColumns}
                        dataSource={form.getFieldValue("allocations") || []}
                        rowKey="id"
                        scroll={{ x: 1200 }}
                        bordered
                        size="small"
                        pagination={false}
                      />
                    )}
                  </Form.Item>

                  {/* Payment Voucher Number - Show after RA Bills are selected */}
                  {selectedRaBills.length > 0 &&
                    ((sequenceAccess?.level === "Site" &&
                      !sequenceAccess?.siteIds?.includes(
                        selectedRaBills[0]?.siteId?.toString()
                      )) ||
                      sequenceAccess?.module === "") && (
                      <div className="mt-3">
                        <Row gutter={[16, 16]}>
                          <Col span={4}>
                            <Form.Item
                              name="raBillPaymentSequenceId"
                              label="Receivable Voucher Number"
                              rules={[
                                {
                                  required: true,
                                  message:
                                    "Receivable voucher number is required",
                                },
                              ]}
                            >
                              <Input
                                placeholder="Enter Receivable Voucher Number"
                                value={paymentSequenceId}
                                onChange={(e) =>
                                  setPaymentSequenceId(e.target.value)
                                }
                              />
                            </Form.Item>
                          </Col>
                          <col span={8}></col>
                        </Row>
                      </div>
                    )}
                </Block>

                <Divider />

                {/* Approval Settings */}
                <Block>
                  <h5>Approval Settings</h5>
                  <Row gutter={[16, 16]}>
                    <Col span={6}>
                      <Form.Item
                        name="approvalRequired"
                        label="Approval Required ?"
                        rules={[
                          {
                            required: true,
                            message: "Please specify if approval is required",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select"
                          onChange={(value: string) => {
                            setApprovalRequired(value);
                            if (value === "false") {
                              setApprovals({ open: false, data: [] });
                            }
                          }}
                        >
                          <Option value="true">Yes</Option>
                          <Option value="false">No</Option>
                        </Select>
                      </Form.Item>

                      {/* Set Approvals Link */}
                      {approvalRequired === "true" && (
                        <Popover
                          content="Click here to set required approvals"
                          title="Approval Required"
                        >
                          <a
                            href="#"
                            className="success"
                            onClick={(e) => {
                              e.preventDefault();
                              if (selectedRaBills.length === 0) {
                                message.error("Please select RA bills first");
                                return;
                              }
                              setApprovals((prev) => ({
                                ...prev,
                                open: true,
                              }));
                            }}
                          >
                            <EditOutlined /> Set Approvals &nbsp;
                            {approvals?.data?.length === 0 ? (
                              <span className="mandatory">
                                Please set approvals
                              </span>
                            ) : (
                              <span className="text-success">
                                ✓ {approvals.data.length} approval(s) configured
                              </span>
                            )}
                          </a>
                        </Popover>
                      )}
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* Commercial Summary */}
                <Block>
                  <h5> Commercial Summary</h5>
                  <Row>
                    <Col span={12}>
                      <Descriptions bordered size="small" column={1}>
                        {/* <Descriptions.Item label="Sum of Allocations">
                          <strong>₹{sumOfAllocations.toLocaleString()}</strong>
                        </Descriptions.Item> */}
                        {/* <Descriptions.Item label="TDS This Payment">
                          <strong className="text-warning">
                            -₹{tdsThisPayment.toLocaleString()}
                          </strong>
                        </Descriptions.Item> */}
                        {/* <Descriptions.Item label="Other Adjustments">
                          <strong>₹{otherAdjustments.toLocaleString()}</strong>
                        </Descriptions.Item> */}
                        <Descriptions.Item label="Net Payable">
                          <strong className="text-success">
                            ₹{netPayable.toLocaleString()}
                          </strong>
                        </Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* File Upload Section */}
                <Block>
                  <h5>Supporting Documents</h5>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="attachments"
                        label="Upload Documents"
                        help="Supported formats: JPG, PNG, PDF, XLSX, DOC, DOCX (Max size: 10MB each)"
                      >
                        <Upload
                          name="file"
                          listType="text"
                          fileList={fileList}
                          beforeUpload={beforeUpload}
                          customRequest={async ({
                            file,
                            onSuccess,
                            onError,
                          }) => {
                            try {
                              const uploadedFile = await handleFileUpload(file);
                              onSuccess && onSuccess(uploadedFile);
                            } catch (error) {
                              onError && onError(error as any);
                            }
                          }}
                          onRemove={handleFileRemove}
                          multiple
                        >
                          <AntButton
                            icon={<UploadOutlined />}
                            loading={uploadLoading}
                            disabled={uploadLoading}
                          >
                            {uploadLoading ? "Uploading..." : "Upload Files"}
                          </AntButton>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                </Block>

                <Divider />

                {/* Action Buttons */}
                <div className="d-flex justify-content-end">
                  <Space>
                    <Button color="secondary" onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                    {/* <Button
                      color="default"
                      onClick={handleSaveAsDraft}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : <SaveOutlined />}
                      Save as Draft
                    </Button> */}
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={loading || selectedRaBills.length === 0}
                    >
                      {loading ? <Spinner size="sm" /> : <></>}
                      Submit
                    </Button>
                  </Space>
                </div>
              </Form>
            </div>
          </div>
        </Card>

        {/* RA Bills Selection Modal */}
        <Modal
          title={`Select RA Bills for Receivable - ${contractorOptions.find((c) => c.value === selectedContractor)
              ?.label || ""
            }`}
          open={showEligibleBills}
          onCancel={cancelRaBillSelection}
          width={1200}
          footer={[
            <AntButton key="cancel" onClick={cancelRaBillSelection}>
              Cancel
            </AntButton>,
            <AntButton
              key="confirm"
              type="primary"
              onClick={confirmRaBillSelection}
              disabled={tempSelectedRaBills.length === 0}
            >
              Add Selected Bills ({tempSelectedRaBills.length})
            </AntButton>,
          ]}
        >
          {raBillsLoading ? (
            <>
              <Flex align="center" gap="middle" className="pageLoader">
                <Spin size="large" className="m-auto" />
              </Flex>
            </>
          ) : (
            <div className="table-responsive poTable">
              <div className="mb-3">
                <AntAlert
                  message={
                    tempSelectedRaBills.length > 0
                      ? `${tempSelectedRaBills.length} RA Bill(s) selected for receivable`
                      : "Select RA Bills to create receivable allocations"
                  }
                  type="info"
                  showIcon
                />
              </div>
              <div
                style={{
                  width: screenSize - 230 + "px",
                  margin: "0px auto 15px auto",
                }}
              >
                <Table
                  className="customTable"
                  columns={raBillColumns}
                  dataSource={eligibleRaBills}
                  rowKey="raBillId"
                  rowSelection={{
                    type: "checkbox",
                    selectedRowKeys: tempSelectedRaBills.map(
                      (bill: any) => bill.raBillId
                    ),
                    onSelect: (record, selected) => {
                      if (selected) {
                        handleTempRaBillSelection(
                          [
                            ...tempSelectedRaBills.map((b: any) => b.raBillId),
                            record.raBillId,
                          ],
                          [...tempSelectedRaBills, record]
                        );
                      } else {
                        const filtered = tempSelectedRaBills.filter(
                          (bill: any) => bill.raBillId !== record.raBillId
                        );
                        handleTempRaBillSelection(
                          filtered.map((b: any) => b.raBillId),
                          filtered
                        );
                      }
                    },
                    onSelectAll: (selected, selectedRows, changeRows) => {
                      if (selected) {
                        handleTempRaBillSelection(
                          eligibleRaBills.map((b) => b.raBillId),
                          eligibleRaBills
                        );
                      } else {
                        handleTempRaBillSelection([], []);
                      }
                    },
                  }}
                  scroll={{ x: 800 }}
                  bordered
                  size="small"
                  pagination={{
                    current: raBillsPagination.current,
                    pageSize: raBillsPagination.pageSize,
                    total: raBillsPagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "30", "40", "50"],
                    onChange: async (newCurrent, newPageSize) => {
                      const direction =
                        newCurrent > raBillsPagination.current ? "F" : "B";
                      const pageDiff =
                        Math.abs(newCurrent - raBillsPagination.current) - 1;

                      await loadEligibleRaBills({
                        current: newCurrent,
                        pageSize: newPageSize,
                        direction,
                        pageDiff,
                        sortOrder: "DESC",
                        paginationId:
                          direction === "F"
                            ? eligibleRaBills[eligibleRaBills.length - 1]
                              ?.paginationId
                            : eligibleRaBills[0]?.paginationId,
                      });
                    },
                  }}
                />
              </div>
              {siteError.show && (
                <div className="mt-3 mb-3">
                  <AntAlert
                    message="Multiple Sites Selected"
                    description={
                      <div>
                        <p>
                          You cannot select RA Bills from different sites in a
                          single receivable.
                        </p>
                        {/* <p>
                          <strong>Sites found in selection:</strong>
                        </p>
                        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
                          {siteError.siteNames.map((name, index) => (
                            <li key={index}>{name}</li>
                          ))}
                        </ul>
                        <p style={{ marginTop: "10px", marginBottom: 0 }}>
                          Please select RA Bills from the same site only.
                        </p> */}
                      </div>
                    }
                    type="error"
                    showIcon
                    closable
                    onClose={() => setSiteError({ show: false, siteNames: [] })}
                  />
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Payments Approval Drawer */}
        {approvals.open && selectedRaBills.length > 0 && (
          <PaymentsApprovalDrawer
            open={approvals.open}
            siteId={selectedRaBills[0]?.siteId || 0}
            onClose={() => {
              setApprovals((prev) => ({
                ...prev,
                open: false,
              }));
            }}
            onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
              setApprovals({
                open: false,
                data: data,
              });
              setIsStrictApproval(isStrictApprovalFromDrawer);
            }}
            initialStrictApproval={isStrictApproval}
            setApprovalSubmissionStatus={(status: boolean) => { }}
            approvalData={approvals.data}
          />
        )}
      </Content>
    </>
  );
};

export default CreateReceivable;
